import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, ListChecks, ReceiptText, HelpCircle, MessageSquareText, Copy, Check, ShieldCheck, AlertTriangle, ClipboardCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtEuro, riskMeta, naturaMeta, API } from "@/lib/okkio";
import Logo from "@/components/Logo";

const TABS = [
  { id: "panoramica", label: "Panoramica" },
  { id: "prezzi", label: "Prezzi" },
  { id: "mancanze", label: "Mancanze" },
  { id: "extracosti", label: "Extracosti" },
  { id: "domande", label: "Domande" },
];

const List = ({ items, tone = "neutral" }) => {
  const dot = { good: "#16A36A", warn: "#F59E0B", crit: "#DC3F4C", info: "#3157F6", neutral: "#94A3B8" }[tone];
  if (!items || items.length === 0) return <p className="text-[#64748B]">Nessun elemento rilevato.</p>;
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={(typeof it === "string" ? it : it.voce || it.descrizione || it.problema || "item") + "-" + i} className="flex items-start gap-3 text-[#334155] leading-relaxed">
          <span className="mt-2 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
          <span>{typeof it === "string" ? it : `${it.voce ? it.voce + " — " : ""}${it.descrizione || it.problema || ""}`}</span>
        </li>
      ))}
    </ul>
  );
};

const Section = ({ id, icon: Icon, title, children }) => (
  <motion.section id={id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
    className="scroll-mt-28 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-7" data-testid={`section-${id}`}>
    <div className="flex items-center gap-3 mb-5">
      <span className="w-10 h-10 rounded-xl bg-[#3157F6]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#3157F6]" strokeWidth={1.9} /></span>
      <h2 className="font-heading text-xl font-semibold text-[#0F172A]">{title}</h2>
    </div>
    {children}
  </motion.section>
);

const Block = ({ title, children }) => (
  <div className="mb-6 last:mb-0">
    <p className="text-sm font-semibold text-[#0F172A] mb-2">{title}</p>
    {children}
  </div>
);

export default function ReportView({ report, embedded = false, children }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const risk = riskMeta[report.livello_rischio] || riskMeta.medio;
  const s = report.sections || {};
  const ap = s.analisi_prezzi || {};
  const comp = s.completezza || {};

  const primaDiFirmare = [
    ...(comp.da_chiarire || []),
    ...(comp.possibili_mancanti || []),
  ].slice(0, 8);

  const copyMsg = () => {
    navigator.clipboard.writeText(s.messaggio_pronto || "");
    setCopied(true); toast.success("Messaggio copiato negli appunti");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div>
      {!embedded && (
        <>
          <div className="h-1.5 w-full bg-[#3157F6]" />
          <header className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center"><Logo className="text-2xl" /></button>
            <a href={`${API}/report/${report.token}/pdf`} target="_blank" rel="noreferrer" data-testid="download-pdf"
              className="inline-flex items-center gap-2 rounded-xl border border-[#3157F6] text-[#3157F6] px-4 py-2.5 text-sm font-semibold hover:bg-[#3157F6] hover:text-white transition-colors">
              <Download className="w-4 h-4" strokeWidth={2} /> Scarica il PDF
            </a>
          </header>
        </>
      )}

      <main className={embedded ? "space-y-5" : "max-w-3xl mx-auto px-5 pb-20 space-y-5"}>
        {report.demo && (
          <div className="rounded-2xl border border-[#E2E8F0] border-dashed bg-white p-5 flex items-start gap-3" data-testid="demo-banner">
            <AlertTriangle className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="font-heading font-semibold text-[#0F172A]">Esempio dimostrativo</p>
              <p className="text-sm text-[#64748B] mt-0.5">Questo è un esempio illustrativo, non un preventivo reale.</p>
            </div>
          </div>
        )}

        {/* Header risultato */}
        <div className="rounded-2xl bg-[#0F172A] text-white p-6 sm:p-8" id="panoramica">
          <span className="text-xs font-semibold tracking-[0.16em] uppercase text-[#8CA0FF]">Risultato OKKIO</span>
          <div className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-4">
            <div className="flex items-end gap-2">
              <span className="font-heading text-6xl font-semibold leading-none" data-testid="score-value">{report.punteggio ?? "—"}</span>
              <span className="text-slate-400 text-base mb-2">/ 100</span>
            </div>
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${risk.cls}`}>
              <AlertTriangle className="w-4 h-4" strokeWidth={2.2} /> {risk.label}
            </span>
            <span className="ml-auto text-sm text-slate-300">Importo: <b className="text-white">{fmtEuro(report.importo_totale)}</b></span>
          </div>
          <p className="mt-5 text-slate-200 leading-relaxed">{report.sintesi}</p>
        </div>

        {/* Tab nav sticky */}
        {!embedded && (
          <nav className="sticky top-0 z-30 -mx-5 px-5 py-3 bg-[#F7F9FC]/90 backdrop-blur-md border-b border-[#E2E8F0]" data-testid="report-tabs">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {TABS.map((t) => (
                <a key={t.id} href={`#${t.id}`}
                  className="whitespace-nowrap rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-semibold text-[#334155] hover:border-[#3157F6] hover:text-[#3157F6] transition-colors">
                  {t.label}
                </a>
              ))}
            </div>
          </nav>
        )}

        {/* Criticità (panoramica) */}
        {report.criticita?.length > 0 && (
          <div className="scroll-mt-28 space-y-3">
            <h2 className="font-heading text-xl font-semibold text-[#0F172A] px-1">Punti da controllare</h2>
            {report.criticita.map((c, i) => {
              const nat = c.natura ? naturaMeta[c.natura] : null;
              const grav = c.gravita === "alta" ? "#DC3F4C" : c.gravita === "media" ? "#F59E0B" : "#64748B";
              return (
                <div key={(c.titolo || "criticita") + "-" + i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid={`criticita-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: grav }} />
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{c.titolo}</h3>
                      <p className="mt-2 text-[#334155] leading-relaxed">{c.descrizione}</p>
                      {nat && <span className={`mt-3 inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${nat.cls}`}>{nat.label}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Section id="prezzi" icon={Scale} title="Analisi prezzi">
          <Block title="Voci apparentemente coerenti"><List items={ap.coerenti} tone="good" /></Block>
          <Block title="Voci in fascia alta"><List items={ap.fascia_alta} tone="warn" /></Block>
          <Block title="Voci non valutabili con affidabilità"><List items={ap.non_valutabili} tone="neutral" /></Block>
        </Section>

        <Section id="mancanze" icon={ListChecks} title="Completezza e mancanze">
          <Block title="Lavorazioni presenti"><List items={comp.presenti} tone="good" /></Block>
          <Block title="Possibili lavorazioni mancanti"><List items={comp.possibili_mancanti} tone="warn" /></Block>
          <Block title="Aspetti da chiarire prima della firma"><List items={comp.da_chiarire} tone="info" /></Block>
        </Section>

        <Section id="extracosti" icon={ReceiptText} title="Possibili extracosti e ambiguità">
          <Block title="Potenziali extracosti"><List items={s.extracosti} tone="warn" /></Block>
          <Block title="Ambiguità"><List items={s.ambiguita} tone="info" /></Block>
        </Section>

        <Section id="domande" icon={HelpCircle} title="Domande da fare all’impresa">
          <ol className="space-y-3">
            {(s.domande_impresa || []).map((q, i) => (
              <li key={q + "-" + i} className="flex gap-3 text-[#334155] leading-relaxed">
                <span className="font-heading font-semibold text-[#3157F6]">{i + 1}.</span><span>{q}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Prima di firmare */}
        {primaDiFirmare.length > 0 && (
          <div className="rounded-2xl border-2 border-[#3157F6]/25 bg-[#3157F6]/[0.04] p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-xl bg-[#3157F6] flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-white" strokeWidth={2} /></span>
              <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Prima di firmare, chiarisci</h2>
            </div>
            <ul className="space-y-3">
              {primaDiFirmare.map((it, i) => (
                <li key={(typeof it === "string" ? it : it.voce || "item") + "-" + i} className="flex items-start gap-3 text-[#0F172A] leading-relaxed">
                  <Check className="w-5 h-5 text-[#3157F6] flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                  <span>{typeof it === "string" ? it : it.voce || ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Messaggio pronto */}
        <Section id="messaggio" icon={MessageSquareText} title="Messaggio pronto per l’impresa">
          <div className="rounded-xl bg-[#F7F9FC] border border-[#E2E8F0] p-5 text-[#334155] whitespace-pre-wrap leading-relaxed">
            {s.messaggio_pronto}
          </div>
          <Button data-testid="copy-message" onClick={copyMsg}
            className="mt-4 w-full sm:w-auto rounded-xl h-14 px-6 text-base font-semibold bg-[#3157F6] hover:bg-[#2444D8] active:scale-[0.98] transition-transform">
            {copied ? <><Check className="w-5 h-5 mr-1.5" /> Messaggio copiato</> : <><Copy className="w-5 h-5 mr-1.5" /> Copia il messaggio per l’impresa</>}
          </Button>
        </Section>

        {children}

        <div className="rounded-2xl bg-white border border-[#E2E8F0] p-6 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#3157F6] flex-shrink-0 mt-0.5" strokeWidth={1.9} />
          <p className="text-sm leading-relaxed text-[#64748B]">
            Valutazione indipendente a scopo informativo, basata sui dati del documento e su riferimenti di mercato.
            Non sostituisce una perizia professionale.
          </p>
        </div>
      </main>
    </div>
  );
}
